/**
 * BulkSelectCourseTable — presentational DataGrid wrapper for the
 * Bulk Add Course Section feature.
 *
 * This file is a thin, stateless shell around MUI's DataGrid.  It owns:
 *   - CDG row-ID resolution (`getRowId` reads `row.data.sectionId`)
 *   - Page numbering conversion (the app uses 1-based pages; DataGrid
 *     uses 0-based internally)
 *   - Zebra-striping via `getRowClassName`
 *   - Custom slot overrides: hidden column headers (the data is
 *     self-explanatory in context), a custom pagination footer, and
 *     a "No results" empty-state overlay
 *   - Conditional footer: the footer is hidden when there are no rows
 *     to avoid showing pagination controls over an empty table
 *
 * It does NOT own data fetching, selection persistence, or filter
 * logic — those live in Table.tsx and index.tsx respectively.
 *
 * Key design decision — `getRowId`:
 *   CDG rows do not have a top-level `id` field; the unique identifier
 *   lives at `row.data.sectionId`.  Rather than mutating every row to
 *   inject an `id` (which would break CDG permission metadata and add
 *   maintenance cost), we configure `getRowId` to derive the ID from
 *   the nested path.  This is the recommended MUI pattern for
 *   non-standard row shapes.
 */
import React from 'react'
import Box from '@mui/material/Box'
import { DataGrid, type GridColDef } from '@mui/x-data-grid'
import { shade, blue, grey } from '../../../../colors'
import CustomFooter from './components/CustomFooter'
import { Typography } from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'

/** Shown inside the DataGrid when the CDG query returns zero rows. */
const NoRowsOverlay: React.FC = () => {
  return (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      flexDirection="column"
      gap={1}
      height="100%"
      py={8}
    >
      <Box
        sx={{
          width: 64,
          height: 64,
          borderRadius: '50%',
          backgroundColor: grey[100],
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mb: 2
        }}
      >
        <SearchIcon sx={{ color: grey[500], fontSize: 32 }} />
      </Box>
      <Typography fontWeight="bold" textAlign="center">No results</Typography>
    </Box>
  )
}

interface CourseTableProps {
  rows: any[]
  columns: Array<GridColDef<any>>
  selectedRows: string[]
  onSelectionChange: (ids: string[]) => void
  page?: number
  pageSize?: number
  rowCount?: number
  pagination?: any
  onPaginationModelChange?: (model: { page: number, pageSize: number }) => void
}

const BulkSelectCourseTable: React.FC<CourseTableProps> = ({ rows, columns, selectedRows, onSelectionChange, page = 1, pageSize = 7, rowCount, pagination, onPaginationModelChange }) => {
  const totalResults = pagination?.totalResults ?? rowCount ?? rows.length
  const numPages = pagination?.numPages ?? Math.ceil(totalResults / pageSize)

  // Convert 1-based page to 0-based for DataGrid
  const gridPage = page - 1

  return (
    <Box sx={{ width: '100%', height: 400 }}>
      <style>{`.even-cell { background-color: ${shade[1]} !important; }`}</style>
      <DataGrid
        rows={rows}
        columns={columns}
        // Derive the unique row key from the CDG nested path instead of
        // expecting a top-level `id`.  This avoids mutating row objects
        // and keeps CDG metadata intact for permission checks.
        getRowId={(row) => row.data.sectionId}
        checkboxSelection
        disableRowSelectionOnClick
        // Controlled selection — the parent (Table.tsx) owns the source
        // of truth so that Select All persistence works across pages.
        rowSelectionModel={selectedRows}
        onRowSelectionModelChange={ids => { onSelectionChange(ids as string[]) }}
        // Page numbering bridge: app = 1-based, DataGrid = 0-based.
        paginationModel={{ page: gridPage, pageSize }}
        onPaginationModelChange={(model) => {
          onPaginationModelChange?.({ page: model.page + 1, pageSize: model.pageSize })
        }}
        pageSizeOptions={[15, 30]}
        // Server-side pagination — we only fetch one page of data at a
        // time from the CDG API; `rowCount` tells the DataGrid the total
        // so it can render the correct number of page links.
        paginationMode="server"
        rowCount={totalResults}
        // Zebra-striping: alternate rows get a subtle background colour.
        getRowClassName={params => {
          const rowIndex = rows.findIndex(row => row.data.sectionId === params.row.data.sectionId)
          if ((rowIndex % 2) === 1) {
            return 'even-cell'
          }
          return ''
        }}
        slots={{
          // Column headers are hidden because the table context makes
          // fields obvious and saves vertical space inside the modal.
          columnHeaders: () => null,
          noRowsOverlay: NoRowsOverlay,
          // Footer is conditionally rendered: hidden on empty state so
          // pagination controls don't appear over the "No results" overlay.
          footer: rows.length > 0
            ? () => (
                <CustomFooter
                  totalResults={totalResults}
                  numPages={numPages}
                  page={page}
                  pageSize={pageSize}
                  onPaginationModelChange={onPaginationModelChange}
                />
              )
            : () => null
        }}
      />
    </Box>
  )
}

export default BulkSelectCourseTable
